import React from 'react';
import AdCreativeThumbnail from '../../components/mi/AdCreativeThumbnail';
import {
  Activity,
  BarChart3,
  Bell,
  Building2,
  FileText,
  Globe,
  Megaphone,
  MoreVertical,
  Newspaper,
  Shield,
  Sparkles,
  Users,
} from 'lucide-react';
import { useNexusData } from './NexusDataContext';

const PLATFORM_AD_META = {
  linkedin: {
    label: 'LinkedIn',
    Icon: Newspaper,
    iconClass: 'nexus-source-icon--linkedin',
    cardClass: 'nexus-source-card--linkedin-ads',
  },
  meta: {
    label: 'Meta',
    Icon: Megaphone,
    iconClass: 'nexus-source-icon--meta',
    cardClass: 'nexus-source-card--meta-ads',
  },
  google: {
    label: 'Google',
    Icon: Globe,
    iconClass: 'nexus-source-icon--google',
    cardClass: 'nexus-source-card--google-ads',
  },
};

const PLATFORM_ADS_DATA_KEY = {
  linkedin: 'linkedinAds',
  meta: 'metaAds',
  google: 'googleAds',
};

function SourceCardMenu() {
  return (
    <button type="button" className="nexus-card-menu nodrag" aria-label="More options">
      <MoreVertical size={16} />
    </button>
  );
}

function CountBadge({ count }) {
  return <span className="nexus-count-badge">{count}</span>;
}

function GroupedFilingRow({ item, isNew = false }) {
  return (
    <>
      <Building2 size={14} className="nexus-list-icon" aria-hidden />
      <div className="nexus-source-list__body">
        <div className="nexus-source-list__row">
          <span className="nexus-source-list__company">{item.company}</span>
          <span className="nexus-source-list__time">
            {isNew ? <span className="nexus-live-dot" /> : null}
            {item.time}
          </span>
        </div>
        <div className="nexus-source-list__meta nexus-source-list__meta--grouped">
          <span className="nexus-source-list__detail--count">{item.detail}</span>
          {item.meta ? (
            <span className="nexus-source-list__meta-sub">{item.meta}</span>
          ) : null}
        </div>
      </div>
    </>
  );
}

export function SerffCard() {
  const { data } = useNexusData();
  const items = data.serff ?? [];

  return (
    <article className="nexus-source-card nexus-source-card--serff">
      <header className="nexus-source-card__head">
        <div className="nexus-source-card__title-row">
          <span className="nexus-source-icon nexus-source-icon--blue">
            <FileText size={18} />
          </span>
          <h3>SERFF Filings</h3>
          <CountBadge count={items.length} />
        </div>
        <SourceCardMenu />
      </header>
      <ul className="nexus-source-list">
        {items.map((item) => (
          <li key={item.company}>
            <GroupedFilingRow item={item} />
          </li>
        ))}
      </ul>
    </article>
  );
}

export function SecCard() {
  const { data } = useNexusData();
  const items = data.sec ?? [];

  return (
    <article className="nexus-source-card nexus-source-card--sec">
      <header className="nexus-source-card__head">
        <div className="nexus-source-card__title-row">
          <span className="nexus-source-icon nexus-source-icon--green">
            <Shield size={18} />
          </span>
          <h3>SEC Filings</h3>
          <CountBadge count={items.length} />
        </div>
        <SourceCardMenu />
      </header>
      <ul className="nexus-source-list">
        {items.map((item) => {
          const linkWhenSingle = (item.filingCount || 1) === 1 && item.documentUrl;
          const row = (
            <GroupedFilingRow item={item} isNew={item.isNew} />
          );
          return (
            <li key={item.company}>
              {linkWhenSingle ? (
                <a
                  href={item.documentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nexus-source-list__link nodrag"
                >
                  {row}
                </a>
              ) : (
                row
              )}
            </li>
          );
        })}
      </ul>
    </article>
  );
}

export function NewsCard() {
  const { data } = useNexusData();
  const items = data.news ?? [];

  return (
    <article className="nexus-source-card nexus-source-card--news">
      <header className="nexus-source-card__head">
        <div className="nexus-source-card__title-row">
          <span className="nexus-source-icon nexus-source-icon--purple">
            <Newspaper size={18} />
          </span>
          <h3>News</h3>
          <CountBadge count={items.length} />
        </div>
        <SourceCardMenu />
      </header>
      <ul className="nexus-news-list">
        {items.map((item) => (
          <li key={item.headline}>
            <div className="nexus-news-list__head">
              <span className="nexus-news-list__title">{item.headline}</span>
              <span className="nexus-source-list__time">{item.time}</span>
            </div>
            <p className="nexus-news-list__summary">{item.summary}</p>
          </li>
        ))}
      </ul>
    </article>
  );
}

function PlatformBadge({ platform }) {
  const label = PLATFORM_AD_META[platform]?.label
    || (platform === 'google' ? 'Google' : platform === 'meta' ? 'Meta' : 'LinkedIn');
  return (
    <span className={`nexus-platform-badge nexus-platform-badge--${platform}`}>{label}</span>
  );
}

function AdTileImage({ ad }) {
  const { adVideoPlaybackEnabled } = useNexusData();
  const variant = ad.imageVariant || ad.image || ad.platform || 'linkedin';

  if (ad.mediaUrl || ad.videoPlaybackUrl) {
    return (
      <AdCreativeThumbnail
        ad={ad}
        playbackEnabled={adVideoPlaybackEnabled}
        imageClassName="nexus-ad-tile__image nexus-ad-tile__image--remote"
        fallbackClassName={`nexus-ad-tile__image nexus-ad-tile__image--${variant}`}
      />
    );
  }

  return <div className={`nexus-ad-tile__image nexus-ad-tile__image--${variant}`} />;
}

function PlatformAdsCard({ platformKey }) {
  const { data } = useNexusData();
  const config = PLATFORM_AD_META[platformKey];
  if (!config) return null;

  const dataKey = PLATFORM_ADS_DATA_KEY[platformKey] || 'linkedinAds';
  const items = data[dataKey] ?? [];
  const { label, Icon, iconClass, cardClass } = config;
  const AdIcon = Icon ?? Megaphone;

  return (
    <article className={`nexus-source-card nexus-source-card--ads ${cardClass}`}>
      <header className="nexus-source-card__head">
        <div className="nexus-source-card__title-row">
          <span className={`nexus-source-icon ${iconClass}`}>
            <AdIcon size={18} />
          </span>
          <h3>{label} Ads</h3>
          <CountBadge count={items.length} />
        </div>
        <SourceCardMenu />
      </header>
      <div className="nexus-ads-list">
        {items.map((ad, index) => (
          <div key={`${ad.company}-${ad.title}-${index}`} className="nexus-ad-tile">
            <AdTileImage ad={ad} />
            <div className="nexus-ad-tile__body">
              <div className="nexus-ad-tile__meta-row">
                <PlatformBadge platform={ad.platform} />
                <span className="nexus-ad-tile__company">{ad.company}</span>
              </div>
              <p className="nexus-ad-tile__title">{ad.title}</p>
              <p className="nexus-ad-tile__sub">{ad.subtitle}</p>
              <p className="nexus-ad-tile__time">{ad.time}</p>
              <button type="button" className="nexus-ad-tile__cta nodrag">
                Learn More
              </button>
            </div>
          </div>
        ))}
      </div>
    </article>
  );
}

export function LinkedinAdsCard() {
  return <PlatformAdsCard platformKey="linkedin" />;
}

export function MetaAdsCard() {
  return <PlatformAdsCard platformKey="meta" />;
}

export function GoogleAdsCard() {
  return <PlatformAdsCard platformKey="google" />;
}

/** @deprecated Legacy export — use LinkedinAdsCard */
export function AdsCard() {
  return <LinkedinAdsCard />;
}

export function ReadoutCard() {
  const { data } = useNexusData();
  const readout = data.readout ?? {};

  return (
    <article className="nexus-readout-card">
      <header className="nexus-readout-card__head">
        <div className="nexus-readout-card__title-row">
          <span className="nexus-readout-card__ai-icon">
            <Sparkles size={20} />
          </span>
          <h2>{readout.title || 'AI Market Readout'}</h2>
        </div>
        <SourceCardMenu />
      </header>
      <div className="nexus-readout-card__status">
        <Activity size={14} aria-hidden />
        <span>
          Synthesized from {readout.synthesizedFrom ?? 0} sources ·{' '}
          {readout.updatedLabel || 'Updated just now'}
        </span>
      </div>
      <ul className="nexus-readout-card__bullets">
        {(readout.bullets ?? []).map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <footer className="nexus-readout-card__actions">
        <button type="button" className="nexus-readout-btn nodrag">
          <BarChart3 size={15} />
          Compare
        </button>
        <button type="button" className="nexus-readout-btn nodrag">
          <Sparkles size={15} />
          Ask AI
        </button>
        <button type="button" className="nexus-readout-btn nodrag">
          <Bell size={15} />
          Create Alert
        </button>
      </footer>
    </article>
  );
}

export function CompetitorsCard() {
  const { data } = useNexusData();
  const competitors = data.competitors ?? [];

  return (
    <article className="nexus-competitors-card">
      <header className="nexus-competitors-card__head">
        <div className="nexus-competitors-card__title-row">
          <Users size={16} aria-hidden />
          <h3>Key Competitors</h3>
        </div>
        <SourceCardMenu />
      </header>
      <div className="nexus-competitors-chips">
        {competitors.map((name) => (
          <span key={name} className="nexus-competitor-chip">
            {name}
          </span>
        ))}
      </div>
    </article>
  );
}
